import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  Eye,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { Link, type MetaFunction } from "react-router";

import { Button } from "@/ui/button";

import { LedgerDemo } from "./ledger-demo";
import { PricingPlans } from "./pricing-plans";

export const meta: MetaFunction = () => {
  return [
    { title: publicEnv.siteTitle },
    {
      name: "description",
      content:
        "A calm family ledger for allowance, spending, and the balance that remains. No kid debit card or bank connection.",
    },
  ];
};

// Explain the product by showing the family ritual rather than abstract features.
export default function Marketing() {
  const startLabel =
    publicEnv.stripeTrialDays > 0
      ? `Start your free ${publicEnv.stripeTrialDays}-day trial`
      : "Create a family ledger";

  return (
    <>
      <section className="grid items-center gap-12 py-18 sm:py-24 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:py-30">
        <div>
          <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
            The family money ledger
          </p>
          <h1 className="mt-5 max-w-3xl font-serif text-5xl leading-[0.98] font-semibold tracking-[-0.035em] sm:text-6xl lg:text-7xl">
            Family money,
            <br />
            made clear.
          </h1>
          <p className="text-muted-foreground mt-6 max-w-xl text-lg leading-8">
            Keep one shared record of what you&apos;ve added, what they&apos;ve
            spent, and what remains. No kid debit card. No bank connection. No
            real money moves.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild className="h-12 px-6 text-base" size="lg">
              <Link to="/login">
                {startLabel}
                <ArrowRight />
              </Link>
            </Button>
            <Button
              asChild
              className="h-12 px-6 text-base"
              size="lg"
              variant="outline"
            >
              <a href="#how-it-works">See how it works</a>
            </Button>
          </div>

          <ul className="text-muted-foreground mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm">
            <li className="flex items-center gap-1.5">
              <Check className="text-income size-4" /> Guardian-managed for
              children
            </li>
            <li className="flex items-center gap-1.5">
              <Check className="text-income size-4" /> Child email optional
            </li>
            <li className="flex items-center gap-1.5">
              <Check className="text-income size-4" /> Guardians stay in control
            </li>
          </ul>
          <p className="mt-4 text-sm">
            <Link
              className="underline-decoration font-medium underline"
              to="/privacy#childrens-privacy"
            >
              Read the Children&apos;s Privacy Notice
            </Link>
          </p>
        </div>

        <LedgerDemo />
      </section>

      <section className="bg-border grid gap-px overflow-hidden rounded-xl border sm:grid-cols-3">
        <TrustPoint icon={ShieldCheck} title="No bank connection">
          Billing stays separate from the family ledger. No financial account is
          connected.
        </TrustPoint>
        <TrustPoint icon={Eye} title="Everyone understands">
          Kids see their balance and the story behind it.
        </TrustPoint>
        <TrustPoint icon={UsersRound} title="One family record">
          Guardians add entries; every change keeps its author.
        </TrustPoint>
      </section>

      <section className="py-24 sm:py-32" id="how-it-works">
        <SectionHeading eyebrow="A small, useful ritual">
          What came in. What went out. What&apos;s left.
        </SectionHeading>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          <StoryCard
            amount="+$10.00"
            icon={ArrowDownLeft}
            number="01"
            title="Add what they earned"
          >
            Record allowance, gifts, or any amount your family agrees belongs in
            their balance.
          </StoryCard>
          <StoryCard
            amount="−$6.50"
            icon={ArrowUpRight}
            number="02"
            title="Record what they spent"
          >
            When you buy something for them, note it while the decision is still
            fresh.
          </StoryCard>
          <StoryCard
            amount="$3.50"
            icon={Eye}
            number="03"
            title="Understand the result"
          >
            The current balance and full history remain visible to the people
            who share the agreement.
          </StoryCard>
        </div>
      </section>

      <section
        className="bg-primary text-primary-foreground overflow-hidden rounded-3xl px-6 py-12 sm:px-10 sm:py-16 lg:px-16"
        id="for-families"
      >
        <div className="grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="font-mono text-xs tracking-[0.16em] uppercase opacity-70">
              Two roles, one truth
            </p>
            <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold sm:text-5xl">
              Clear for adults.
              <br />
              Kind to kids.
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <RoleCard title="Guardians">
              See every child at a glance, record entries in seconds, invite
              another adult, and correct mistakes without erasing history.
            </RoleCard>
            <RoleCard title="Kids">
              See one calm balance, a plain-language timeline, and who recorded
              each change—with no administrative clutter.
            </RoleCard>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" id="pricing">
        <div className="grid items-end gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <SectionHeading eyebrow="Simple family pricing">
            One plan. Every family feature.
          </SectionHeading>
          <p className="text-muted-foreground max-w-xl leading-7 lg:justify-self-end">
            One subscription covers the household, including guardian access,
            child ledgers, recurring allowance, and the full family history.
          </p>
        </div>

        <PricingPlans className="mt-12" />
        <p className="mt-6 text-center text-sm">
          <Link
            className="underline-decoration text-primary inline-flex items-center gap-1.5 font-medium hover:underline"
            to="/pricing"
          >
            See full pricing details
            <ArrowRight className="size-4" />
          </Link>
        </p>
      </section>

      <section className="grid gap-12 py-24 sm:py-32 lg:grid-cols-[0.75fr_1.25fr]">
        <SectionHeading eyebrow="Good to know">
          A ledger, not a bank.
        </SectionHeading>

        <div className="divide-y border-y">
          <Faq question="Is any real money held here?">
            No. {publicEnv.siteTitle} records a family&apos;s understanding of a
            balance. It does not hold funds, transfer money, or replace a bank
            account.
          </Faq>
          <Faq question="Does my child need an email address?">
            No. You can create a child ledger with only a name. Add a sign-in
            email later if they are ready for their own access.
          </Faq>
          <Faq question="Can another guardian help?">
            Yes. Add their email to the family so they can record entries and
            see the same household history.
          </Faq>
          <Faq question="Does one plan cover the whole family?">
            Yes. The subscription belongs to the household, not an individual
            child. Guardians manage billing for everyone in the family.
          </Faq>
          <Faq question="What if someone makes a mistake?">
            Guardians can fix details such as the description, category, or
            place. Amount, direction, or date changes preserve the original
            beside its replacement, and an unwanted entry can be reversed
            without silently deleting the history.
          </Faq>
        </div>
      </section>

      <section className="bg-secondary/70 mb-24 rounded-3xl px-6 py-14 text-center sm:px-10 sm:py-18">
        <p className="font-mono text-xs font-semibold tracking-[0.16em] uppercase">
          Start your family ledger with one simple entry
        </p>
        <h2 className="mx-auto mt-4 max-w-2xl font-serif text-4xl font-semibold sm:text-5xl">
          Make sense of every cent.
        </h2>
        <Button asChild className="mt-8 h-12 px-6 text-base" size="lg">
          <Link to="/login">
            {startLabel}
            <ArrowRight />
          </Link>
        </Button>
      </section>
    </>
  );
}

interface TrustPointProps {
  children: string;
  icon: typeof ShieldCheck;
  title: string;
}

// Present a concrete trust boundary in a compact strip.
function TrustPoint({ children, icon: Icon, title }: TrustPointProps) {
  return (
    <div className="bg-card flex gap-3 p-5 sm:p-6">
      <Icon className="text-primary mt-0.5 size-5 shrink-0" />
      <div>
        <h2 className="font-medium">{title}</h2>
        <p className="text-muted-foreground mt-1 text-sm leading-6">
          {children}
        </p>
      </div>
    </div>
  );
}

interface SectionHeadingProps {
  children: string;
  eyebrow: string;
}

// Keep section hierarchy editorial and concise.
function SectionHeading({ children, eyebrow }: SectionHeadingProps) {
  return (
    <div className="max-w-3xl">
      <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
        {eyebrow}
      </p>
      <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold sm:text-5xl">
        {children}
      </h2>
    </div>
  );
}

interface StoryCardProps {
  amount: string;
  children: string;
  icon: typeof ArrowDownLeft;
  number: string;
  title: string;
}

// Connect a household action to its visible effect on the ledger.
function StoryCard({
  amount,
  children,
  icon: Icon,
  number,
  title,
}: StoryCardProps) {
  return (
    <article className="panel-shadow bg-card flex min-h-72 flex-col rounded-2xl border p-6">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground font-mono text-xs">
          {number}
        </span>
        <Icon className="text-primary size-5" />
      </div>
      <p className="money-numbers mt-8 font-serif text-4xl font-semibold">
        {amount}
      </p>
      <h3 className="mt-auto pt-8 text-lg font-semibold">{title}</h3>
      <p className="text-muted-foreground mt-2 text-sm leading-6">{children}</p>
    </article>
  );
}

interface RoleCardProps {
  children: string;
  title: string;
}

// Describe the relevant experience without exposing the other role's clutter.
function RoleCard({ children, title }: RoleCardProps) {
  return (
    <article className="border-primary-foreground/20 bg-primary-foreground/10 rounded-2xl border p-6">
      <h3 className="font-serif text-2xl font-semibold">{title}</h3>
      <p className="mt-3 text-sm leading-7 opacity-80">{children}</p>
    </article>
  );
}

interface FaqProps {
  children: string;
  question: string;
}

// Let visitors answer trust questions without introducing another UI library.
function Faq({ children, question }: FaqProps) {
  return (
    <details className="group py-5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
        {question}
        <span className="text-primary text-xl transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <p className="text-muted-foreground max-w-2xl pt-3 text-sm leading-7">
        {children}
      </p>
    </details>
  );
}
